import React, {startTransition, useState} from "react";
import {Link} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";
import {tokenSelector} from "../../Recoil/Atom/authState";
import {useGetQuery} from "../../hooks/useQueryHooks";
import {ResponseList} from "../../models/clientModels/interfaces/Response";

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
} from "@mui/material";
import * as changeRequestApi from "../../api/workOrderApi/changeRequestApi";
import ActionButtonGroup from "../../components/ActionButtonGroup";
import MWSkeleton from "../../components/MWSkeleton";
import SubTitle from "../../components/SubTitle";
import {changeRequestListActionButton} from "../../models/clientModels/data/actionButtonData";
import {FilterGroup, StyledTable, SubMenuGroup} from "../../styledComponents/StyledListContainer";
import {PaginationContainer} from "../../styledComponents/StyledPagination";

import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {Controller, useForm} from "react-hook-form";
import {useDepartmentList} from "../../hooks/useDepartmentList";
import {changeValue} from "../../models/clientModels/data/selectList";
import {
  EQChangeRequest,
  changeRequestListParams,
  changeStatusNames,
  changeTypeNames,
  initializeChangeRequestListParams,
} from "../../models/serverModels/workOrder/EQChangeRequest";
import {StyledTableContainer, StyledTableHead} from "../../styledComponents/StyledTable";
import ko from "date-fns/locale/ko";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1500px",
}));

const ChangeRequestList = () => {
  const tokenState = useRecoilValue(tokenSelector);

  const setAlertState = useSetRecoilState(alertStateAtom);

  const [page, setPage] = useState<number>(1);

  const [filter, setFilter] = useState<changeRequestListParams>(initializeChangeRequestListParams);

  const queryString = Object.entries(filter)
    .map((e) => e.join("="))
    .join("&");

  const {departmentList} = useDepartmentList();

  const {
    data: changeRequestList,
    isLoading,
    isFetching,
  } = useGetQuery<ResponseList<EQChangeRequest>>({
    key: ["ChangeRequestList", {...filter, page, token: tokenState}],
    api: () => changeRequestApi.getList({...filter, page, token: tokenState}),
  });

  const departmentHandler = (event: SelectChangeEvent<number>) => {
    setFilter({
      ...filter,
      page: 1,
      [event.target.name]: event.target.value,
    });
  };

  const {handleSubmit, control, getValues, watch, reset, setValue} =
    useForm<changeRequestListParams>();

  const selectChangeHandler = async (event: SelectChangeEvent<any>) => {
    startTransition(() => {
      setValue("type", event.target.value);
      setFilter({...filter, page: 1, type: event.target.value});
    });
  };

  const changeRequestFilteringHandler = (
    event: React.BaseSyntheticEvent<Event, EventTarget & HTMLInputElement>
  ) => {
    setValue(event.target.name, event.target.value);
    setFilter({...filter, page: 1, [event.target.name]: event.target.value});
  };

  const startDateHandler = (newValue: Date | null) => {
    setFilter({
      ...filter,
      page: 1,
      startDate: newValue,
    });
  };

  const endDateHandler = (newValue: Date | null) => {
    setFilter({
      ...filter,
      page: 1,
      endDate: newValue,
    });
  };

  const resetHandler = () => {
    setFilter(initializeChangeRequestListParams);
    reset();
  };

  const paginationHandleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilter({...filter, page: value});
    setPage(value);
  };

  return (
    <ListContainer>
      <SubTitle subTitle="변경이력서 관리" />
      <SubMenuGroup>
        <FilterGroup>
          <FormControl>
            <InputLabel id="type">변경구분</InputLabel>
            <Select
              name="type"
              id="type"
              label="변경구분"
              size="small"
              value={filter?.type}
              onChange={selectChangeHandler}
            >
              {changeValue.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id="departmentUID">대상부서</InputLabel>
            <Select
              name="departmentUID"
              id="departmentUID"
              label="대상부서"
              size="small"
              value={filter?.departmentUID}
              onChange={departmentHandler}
            >
              <MenuItem value={-1}>전체</MenuItem>
              {departmentList?.items.map((item) => (
                <MenuItem key={item.uid} value={item.uid}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box>
            <Controller
              name="userName"
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  label="발의자"
                  size="small"
                  onChange={changeRequestFilteringHandler}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="content"
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  label="변경내용"
                  size="small"
                  onChange={changeRequestFilteringHandler}
                />
              )}
            />
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
            <Box>
              <DatePicker
                inputFormat={"yyyy년MM월dd일"}
                label="시작일"
                value={filter.startDate}
                onChange={startDateHandler}
                renderInput={(params) => <TextField size="small" {...params} />}
              />
            </Box>
            <Box>
              <DatePicker
                inputFormat={"yyyy년MM월dd일"}
                label="종료일"
                value={filter.endDate}
                onChange={endDateHandler}
                renderInput={(params) => <TextField size="small" {...params} />}
              />
            </Box>
          </LocalizationProvider>
          <Button
            onClick={resetHandler}
            variant="contained"
            color="success"
            size="small"
            sx={{whiteSpace: "nowrap"}}
          >
            초기화
          </Button>
        </FilterGroup>
        <ActionButtonGroup data={changeRequestListActionButton(queryString)} />
      </SubMenuGroup>
      <StyledTableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              <TableCell align="center">#</TableCell>
              <TableCell align="center">변경 No</TableCell>
              <TableCell align="center">변경구분</TableCell>
              <TableCell align="center">발의자</TableCell>
              <TableCell align="center">발의부서</TableCell>
              <TableCell align="center">변경내용</TableCell>
              <TableCell align="center">변경등급</TableCell>
              <TableCell align="center">단계</TableCell>
              <TableCell align="center">변경검토근거자료</TableCell>
              <TableCell align="center">변경요구서</TableCell>
              <TableCell align="center">공정안전자료</TableCell>
              <TableCell align="center">공정위험성평가</TableCell>
              <TableCell align="center">안전운전절차</TableCell>
              <TableCell align="center">비상조치계획</TableCell>
              <TableCell align="center">근로자교육</TableCell>
              <TableCell align="center">가동전점검</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={17} column={16} />
            ) : (
              changeRequestList?.items.map((item) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">
                    {item.uid}
                    {/* {page === 1
                      ? index + 1
                      : (page - 1) * changeRequestListParams.rows! + index + 1} */}
                  </TableCell>
                  <TableCell align="center">
                    <Link to={`/WorkOrder/EditChangeRequest/${item.uid}`}>{item.serialString}</Link>
                  </TableCell>
                  <TableCell align="left">
                    {changeTypeNames.find((v, idx) => (item.type === idx ? v : null))} <br />
                    {new Date(item?.completeDate).getFullYear()}년
                    {new Date(item?.completeDate).getMonth() + 1}월
                    {new Date(item?.completeDate).getDate()}일
                  </TableCell>
                  <TableCell align="center">{item.requester.name}</TableCell>

                  <TableCell align="center">{item.department.name}</TableCell>
                  <TableCell align="left">{item.content}</TableCell>
                  <TableCell align="center">{item.level}</TableCell>
                  <TableCell align="center">
                    {changeStatusNames.find((v, idx) => (item.status === idx ? v : null))}
                  </TableCell>
                  <TableCell align="left">
                    <Link to="">{item.attachmentFileName}</Link>
                  </TableCell>
                  <TableCell align="center">
                    {item.requestFile ? <Link to="">O</Link> : "X"}
                  </TableCell>
                  <TableCell align="center">
                    {item.processSafetyFile ? (
                      "해당없음"
                    ) : item.hasProcessSafety ? (
                      <Link to="">O</Link>
                    ) : (
                      "미등록"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {item.hasProcessEval ? (
                      "해당없음"
                    ) : item.processFile ? (
                      <Link to="">O</Link>
                    ) : (
                      "미등록"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {item.hasSafetyProcedure ? (
                      "해당없음"
                    ) : item.procedureFile ? (
                      <Link to="">O</Link>
                    ) : (
                      "미등록"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {item.hasEmergency ? (
                      "해당없음"
                    ) : item.emergencyFile ? (
                      <Link to="">O</Link>
                    ) : (
                      "미등록"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {item.hasEducation ? (
                      "해당없음"
                    ) : item.educationFile ? (
                      <Link to="">O</Link>
                    ) : (
                      "미등록"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {item.preCheckFile ? <Link to="">O</Link> : "X"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      {/* 페이지네이션 */}
      <PaginationContainer>
        <Pagination
          count={changeRequestList?.maxPage}
          page={page}
          onChange={paginationHandleChange}
          variant="outlined"
          color="primary"
          shape="rounded"
        />
      </PaginationContainer>
    </ListContainer>
  );
};

export default ChangeRequestList;
